import React, { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text, useToast, ButtonGroup, Button, Icon } from '@chakra-ui/react';
import { FaTable, FaColumns } from 'react-icons/fa';
import TicketListPage from '../components/TicketListPage';
import TicketKanbanView from '../components/TicketKanbanView';
import TicketDashboard from '../components/TicketDashboard';
import ScrollToTop from "../components/ScrollToTop";
import { getTickets, getTicketStats } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';

const FILTERS_KEY = 'tickets_filters';

const getSavedFilters = () => {
    try {
        const saved = sessionStorage.getItem(FILTERS_KEY);
        return saved ? JSON.parse(saved) : {};
    } catch {
        return {};
    }
};

// "Meus Tickets" e "Atribuido Para" batem no mesmo filtro do backend
// (atribuicoes.usuario_id), entao os dois precisam apontar para a mesma pessoa.
const getUsuarioLogadoId = () => {
    try {
        const salvo = window.localStorage.getItem('user');
        const usuario = salvo ? JSON.parse(salvo) : null;
        return usuario?.id ? String(usuario.id) : '';
    } catch {
        return '';
    }
};

const Tickets = () => {
    const [tickets, setTickets] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [paginationData, setPaginationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);
    const [viewMode, setViewMode] = useState(() => {
        return window.localStorage.getItem('tickets_view_mode') || 'table';
    });

    const _f = getSavedFilters();
    const [searchTerm, setSearchTerm] = useState(_f.searchTerm ?? '');
    const [statusFilter, setStatusFilter] = useState(_f.statusFilter ?? '');
    const [priorityFilter, setPriorityFilter] = useState(_f.priorityFilter ?? '');
    const _meusTickets = _f.showMyTickets ?? true;
    const [showMyTickets, setShowMyTickets] = useState(_meusTickets);
    const [showMlReclamacoes, setShowMlReclamacoes] = useState(_f.showMlReclamacoes ?? false);
    const [startDate, setStartDate] = useState(_f.startDate ?? '');
    const [endDate, setEndDate] = useState(_f.endDate ?? '');
    const [currentPage, setCurrentPage] = useState(_f.currentPage ?? 1);
    const [createdByFilter, setCreatedByFilter] = useState(_f.createdByFilter ?? '');
    const [assignedToFilter, setAssignedToFilter] = useState(
        _f.assignedToFilter ?? (_meusTickets ? getUsuarioLogadoId() : '')
    );
    const [sortOption, setSortOption] = useState(_f.sortOption ?? 'default');
    const [activityFilter, setActivityFilter] = useState(_f.activityFilter ?? '');

    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const toast = useToast();

    useEffect(() => {
        sessionStorage.setItem(FILTERS_KEY, JSON.stringify({
            searchTerm,
            statusFilter,
            priorityFilter,
            showMyTickets,
            showMlReclamacoes,
            startDate,
            endDate,
            currentPage,
            createdByFilter,
            assignedToFilter,
            sortOption,
            activityFilter,
        }));
    }, [searchTerm, statusFilter, priorityFilter, showMyTickets, showMlReclamacoes, startDate, endDate, currentPage, createdByFilter, assignedToFilter, sortOption, activityFilter]);

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        window.localStorage.setItem('tickets_view_mode', mode);
        setCurrentPage(1);
    };

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        const params = {
            page: viewMode === 'kanban' ? 1 : currentPage,
            per_page: viewMode === 'kanban' ? 9999 : 50,
            search: debouncedSearchTerm,
            status: viewMode === 'kanban' ? '' : statusFilter,
            priority: priorityFilter,
            my_tickets: showMyTickets,
            ml_reclamacoes: showMlReclamacoes,
            startDate: startDate,
            endDate: endDate,
            createdBy: createdByFilter,
            assignedTo: assignedToFilter,
            sort: viewMode === 'kanban' ? 'updated_desc' : sortOption,
            activityDays: activityFilter,
        };
        try {
            const response = await getTickets(params);
            setTickets(response.data.data);
            setPaginationData(response.data.meta);
        } catch (error) {
            setTickets([]);
            setPaginationData(null);
            toast({
                title: "Erro ao buscar tickets",
                description: error.response?.data?.message || "Nao foi possivel carregar a lista.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [
        currentPage,
        debouncedSearchTerm,
        statusFilter,
        priorityFilter,
        showMyTickets,
        showMlReclamacoes,
        startDate,
        endDate,
        toast,
        createdByFilter,
        assignedToFilter,
        sortOption,
        activityFilter,
        viewMode,
    ]);

    const fetchDashboardStats = useCallback(async () => {
        setLoadingStats(true);
        try {
            const response = await getTicketStats();
            setDashboardStats(response.data.data);
        } catch (error) {
            toast({
                title: "Erro ao buscar metricas",
                description: error.response?.data?.message || "Nao foi possivel carregar os totais.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoadingStats(false);
        }
    }, [toast]);

    const handleDataRefresh = useCallback(() => {
        fetchTickets();
        fetchDashboardStats();
    }, [fetchTickets, fetchDashboardStats]);

    // Escolher outra pessoa em "Atribuido Para" desmarca "Meus Tickets"; sem isso o
    // backend somava as duas condicoes e a lista voltava vazia.
    const handleAssignedToChange = useCallback((valor) => {
        setAssignedToFilter(valor);
        setShowMyTickets(valor !== '' && valor === getUsuarioLogadoId());
    }, []);

    const handleShowMyTicketsChange = useCallback((marcado) => {
        setShowMyTickets(marcado);
        setAssignedToFilter(marcado ? getUsuarioLogadoId() : '');
    }, []);

    const handleClearFilters = useCallback(() => {
        setSearchTerm('');
        setStatusFilter('');
        setPriorityFilter('');
        setShowMyTickets(false);
        setShowMlReclamacoes(false);
        setStartDate('');
        setEndDate('');
        setCreatedByFilter('');
        setAssignedToFilter('');
        setSortOption('default');
        setActivityFilter('');
        setCurrentPage(1);
        sessionStorage.removeItem(FILTERS_KEY);
    }, []);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);


    return (
        <>
            <ScrollToTop />
            <Flex justify="center" align="start" bg="gray.50" minH="100vh" p={{ base: 2, md: 4 }}>
                <Box w={{ base: "100%", lg: "95%", xl: "90%" }} maxW="1800px">
                    <Box textAlign="left" my={4}>
                        <Text color={"#F68633"} fontWeight={"bold"} fontSize="2xl">
                            PAINEL DE TICKETS
                        </Text>
                    </Box>

                    <TicketDashboard stats={dashboardStats} loading={loadingStats} />

                    <TicketListPage
                        tickets={tickets}
                        paginationData={paginationData}
                        loading={loading}
                        filters={{
                            searchTerm,
                            statusFilter: viewMode === 'kanban' ? '' : statusFilter,
                            priorityFilter,
                            showMyTickets,
                            showMlReclamacoes,
                            startDate,
                            endDate,
                            currentPage,
                            createdByFilter,
                            assignedToFilter,
                            sortOption,
                            activityFilter,
                        }}
                        setFilters={{
                            setSearchTerm,
                            setStatusFilter,
                            setPriorityFilter,
                            setShowMyTickets: handleShowMyTicketsChange,
                            setShowMlReclamacoes,
                            setStartDate,
                            setEndDate,
                            setCurrentPage,
                            setCreatedByFilter,
                            setAssignedToFilter: handleAssignedToChange,
                            setSortOption,
                            setActivityFilter,
                        }}
                        onUpdateSuccess={handleDataRefresh}
                        onClearFilters={handleClearFilters}
                        hideTable={viewMode === 'kanban'}
                        viewToggle={
                            <Flex justify="center" mt={2} mb={3}>
                                <ButtonGroup size="sm" isAttached variant="outline">
                                    <Button
                                        leftIcon={<Icon as={FaTable} />}
                                        onClick={() => handleViewModeChange('table')}
                                        bg={viewMode === 'table' ? 'blue.500' : 'white'}
                                        color={viewMode === 'table' ? 'white' : 'gray.600'}
                                        _hover={{ bg: viewMode === 'table' ? 'blue.600' : 'gray.100' }}
                                        borderColor="gray.300"
                                    >
                                        Tabela
                                    </Button>
                                    <Button
                                        leftIcon={<Icon as={FaColumns} />}
                                        onClick={() => handleViewModeChange('kanban')}
                                        bg={viewMode === 'kanban' ? 'blue.500' : 'white'}
                                        color={viewMode === 'kanban' ? 'white' : 'gray.600'}
                                        _hover={{ bg: viewMode === 'kanban' ? 'blue.600' : 'gray.100' }}
                                        borderColor="gray.300"
                                    >
                                        Kanban
                                    </Button>
                                </ButtonGroup>
                            </Flex>
                        }
                    />

                    {viewMode === 'kanban' && (
                        <Box mb={16}>
                            <TicketKanbanView
                                tickets={tickets}
                                loading={loading}
                                onUpdateSuccess={handleDataRefresh}
                            />
                        </Box>
                    )}
                </Box>
            </Flex>
        </>
    );
};

export default Tickets;
